import { logger } from '@accedo/logger';
import { Category, Genre, Item } from '##/dataModels/item';
import { Movie } from '##/dataModels/movie';
import { Show } from '##/dataModels/show';
import { ShortFilm } from '##/dataModels/shortFilm';
import { Episode } from '##/dataModels/episode';
import { Season } from '##/dataModels/season';
import { MediaItem } from '##/dataModels/mediaItem';
import { CacheService } from '##/services/cache';
import { getCacheKey } from '##/services/elevate/utils';
import { apiCall as nzosApiCall } from '##/services/nzos/adapter';
import { getNZOSBaseUrl } from '##/services/nzos/config';
import {
  OVPService,
  OVPSearchResponse,
  OVPPaginationResponse,
  SearchOptions,
  OVPOptions,
  EpisodeOptions,
} from '../../../types';

const LP = '[NzosOVPService]';

// Cache TTL constants
const SHORT_TTL = 3600; // 1 hour
const MEDIUM_TTL = 10800; // 3 hours
const LONG_TTL = 86400; // 1 day

const ovpCache = CacheService.createCache({
  cacheType: 'localStorage',
  prefix: 'nzos-ovp',
  ttlSeconds: LONG_TTL,
  automaticCleanup: true,
  flushOnStart: true,
});

/**
 * NZOS OVP Service implementation.
 *
 * Provides content discovery and retrieval from NZOS middleware API.
 */
class NzosOVPService implements OVPService {
  // eslint-disable-next-line no-use-before-define
  private static instance: NzosOVPService;

  private constructor() {
    logger.debug(`${LP} Creating new instance`);
  }

  static getInstance(): NzosOVPService {
    if (!NzosOVPService.instance) {
      NzosOVPService.instance = new NzosOVPService();
    }
    return NzosOVPService.instance;
  }

  /**
   * Clear cache for a specific movie.
   * @param id - Movie identifier.
   */
  static async clearMovieCache(id: string): Promise<void> {
    const baseUrl = getNZOSBaseUrl();
    const endpointPath = `/ovp/movies/${id}`;
    const cacheKey = getCacheKey(baseUrl, endpointPath);
    await ovpCache.remove(cacheKey);
    logger.debug(`${LP} Cleared cache for movie: ${id}`);
  }

  /**
   * Clear cache for a specific short film.
   * @param id - Short film identifier.
   */
  static async clearShortFilmCache(id: string): Promise<void> {
    const baseUrl = getNZOSBaseUrl();
    const endpointPath = `/ovp/short-films/${id}`;
    const cacheKey = getCacheKey(baseUrl, endpointPath);
    await ovpCache.remove(cacheKey);
    logger.debug(`${LP} Cleared cache for short film: ${id}`);
  }

  /**
   * Clear cache for a specific show.
   * @param id - Show identifier.
   */
  static async clearShowCache(id: string): Promise<void> {
    const baseUrl = getNZOSBaseUrl();
    const endpointPath = `/ovp/shows/${id}`;
    const cacheKey = getCacheKey(baseUrl, endpointPath);
    await ovpCache.remove(cacheKey);
    logger.debug(`${LP} Cleared cache for show: ${id}`);
  }

  /**
   * Search content by keyword and optional filters.
   * @param keyword - Search keyword.
   * @param options - Search options (itemTypes, abortControllerSignal).
   * @param authToken - Optional authorization token for authenticated requests.
   * @returns Search results with movies, shorts, and shows.
   */
  async search(keyword: string, options?: SearchOptions, authToken?: string): Promise<OVPSearchResponse> {
    const baseUrl = getNZOSBaseUrl();
    const endpoint = '/ovp/search';
    const params = {
      keyword,
      itemTypes: options?.itemTypes || ['movie', 'show', 'short-film'],
      pageSize: options?.pageSize,
      pageNumber: options?.pageNumber,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    };

    const cacheKey = getCacheKey(baseUrl, endpoint, params);
    const cachedResponse = await ovpCache.get<OVPSearchResponse>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await this.apiCall<OVPSearchResponse>(endpoint, params, authToken);
    ovpCache.set(cacheKey, response, SHORT_TTL);
    return response;
  }

  /**
   * Search content by specific type.
   * @param keyword - Search keyword.
   * @param itemType - Item type (movie, show, short).
   * @param options - Search options.
   * @returns Paginated search results.
   */
  async searchByType(keyword: string, itemType: string, options?: OVPOptions): Promise<OVPPaginationResponse<Item>> {
    const baseUrl = getNZOSBaseUrl();
    const endpoint = '/ovp/search';
    const params = {
      keyword,
      itemTypes: [itemType],
      pageSize: options?.pageSize,
      pageNumber: options?.pageNumber,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    };

    const cacheKey = getCacheKey(baseUrl, endpoint, params);
    const cachedResponse = await ovpCache.get<OVPPaginationResponse<Item>>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const searchResponse = await this.apiCall<OVPSearchResponse>(endpoint, params);

    // Transform search response to pagination response.
    let items: Item[] = [];
    if (itemType === 'movie') {
      items = (searchResponse.movies || []) as Item[];
    } else if (itemType === 'show') {
      items = (searchResponse.shows || []) as Item[];
    } else {
      items = (searchResponse.shortFilms || []) as Item[];
    }

    const paginationResponse: OVPPaginationResponse<Item> = {
      items,
      totalCount: items.length,
      pageNumber: options?.pageNumber || 1,
      pageSize: options?.pageSize || items.length,
      id: '',
      type: '',
    };

    ovpCache.set(cacheKey, paginationResponse, SHORT_TTL);
    return paginationResponse;
  }

  /**
   * Get assets by category.
   * @param categoryId - Category identifier.
   * @param opts - Pagination options.
   * @returns Paginated list of media items.
   */
  async getAssetsByCategory(categoryId: string, opts?: OVPOptions): Promise<OVPPaginationResponse<MediaItem>> {
    const baseUrl = getNZOSBaseUrl();
    const endpointPath = `/ovp/categories/${categoryId}/assets`;
    const params = {
      pageSize: opts?.pageSize,
      pageNumber: opts?.pageNumber,
      sortBy: opts?.sortBy,
      sortOrder: opts?.sortOrder,
    };

    const cacheKey = getCacheKey(baseUrl, endpointPath, params);
    const cachedResponse = await ovpCache.get<OVPPaginationResponse<MediaItem>>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await this.apiCall<OVPPaginationResponse<MediaItem>>(endpointPath, params);
    ovpCache.set(cacheKey, response, MEDIUM_TTL);
    return response;
  }

  /**
   * Get paginated list of movies.
   * @param options - Pagination options.
   * @returns Paginated movie list.
   */
  async getMovies(options?: OVPOptions): Promise<OVPPaginationResponse<Movie>> {
    const baseUrl = getNZOSBaseUrl();
    const endpoint = '/ovp/movies';
    const params = {
      pageSize: options?.pageSize,
      pageNumber: options?.pageNumber,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    };

    const cacheKey = getCacheKey(baseUrl, endpoint, params);
    const cachedResponse = await ovpCache.get<OVPPaginationResponse<Movie>>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await this.apiCall<OVPPaginationResponse<Movie>>(endpoint, params);
    ovpCache.set(cacheKey, response, MEDIUM_TTL);
    return response;
  }

  /**
   * Get movie by ID.
   * @param id - Movie identifier.
   * @returns Movie details.
   */
  async getMovie(id: string, authToken?: string): Promise<Movie> {
    const baseUrl = getNZOSBaseUrl();
    const endpointPath = `/ovp/movies/${id}`;
    const cacheKey = getCacheKey(baseUrl, endpointPath);
    const cachedResponse = await ovpCache.get<Movie>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await this.apiCall<Movie>(endpointPath, undefined, authToken);
    ovpCache.set(cacheKey, response, LONG_TTL);
    return response;
  }

  /**
   * Get short film by ID.
   * @param id - Short film identifier.
   * @param authToken - Optional authentication token for protected content.
   * @returns Short film details.
   */
  async getShortFilm(id: string, authToken?: string): Promise<ShortFilm> {
    const baseUrl = getNZOSBaseUrl();
    const endpointPath = `/ovp/short-films/${id}`;
    const cacheKey = getCacheKey(baseUrl, endpointPath);
    const cachedResponse = await ovpCache.get<ShortFilm>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await this.apiCall<ShortFilm>(endpointPath, undefined, authToken);
    ovpCache.set(cacheKey, response, LONG_TTL);
    return response;
  }

  /**
   * Get show by ID.
   * @param id - Show identifier.
   * @returns Show details.
   */
  async getShow(id: string, authToken?: string): Promise<Show> {
    const baseUrl = getNZOSBaseUrl();
    const endpointPath = `/ovp/shows/${id}`;
    const cacheKey = getCacheKey(baseUrl, endpointPath);
    const cachedResponse = await ovpCache.get<Show>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await this.apiCall<Show>(endpointPath, undefined, authToken);
    ovpCache.set(cacheKey, response, LONG_TTL);
    return response;
  }

  /**
   * Get seasons for a show.
   * @param showId - Show identifier.
   * @param options - Pagination options.
   * @param authToken - Optional authentication token.
   * @param seasonsUrl - Seasons URL already known to the caller. Skips the internal getShow call.
   * @returns Paginated season list.
   */
  async getSeasons(
    showId: string,
    options?: OVPOptions,
    authToken?: string,
    seasonsUrl?: string,
  ): Promise<OVPPaginationResponse<Season>> {
    // If a seasons URL was supplied by the caller, use it directly and skip getShow.
    // seasonsRef may be a string URL at runtime even though Show['seasons'] is typed Season[].
    const seasonsRef = seasonsUrl ?? (await this.getShow(showId, authToken)).seasons;

    // If seasons is a URL string, fetch from that URL
    if (typeof seasonsRef === 'string') {
      const baseUrl = getNZOSBaseUrl();
      const cacheKey = getCacheKey(baseUrl, seasonsRef);
      const cachedResponse = await ovpCache.get<OVPPaginationResponse<Season>>(cacheKey);
      if (cachedResponse) {
        return cachedResponse;
      }

      const response = await this.fetch(seasonsRef, options, authToken);
      const paginationResponse: OVPPaginationResponse<Season> = {
        items: response.items as Season[],
        totalCount: response.totalCount,
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        id: response.id,
        type: response.type,
      };

      ovpCache.set(cacheKey, paginationResponse, LONG_TTL);
      return paginationResponse;
    }

    // If seasons is already an array, return it as pagination response
    const seasons = Array.isArray(seasonsRef) ? seasonsRef : [];
    const paginationResponse: OVPPaginationResponse<Season> = {
      items: seasons,
      totalCount: seasons.length,
      pageNumber: options?.pageNumber || 1,
      pageSize: options?.pageSize || seasons.length,
      id: '',
      type: '',
    };

    return paginationResponse;
  }

  /**
   * Get season by ID.
   * @param seasonId - Season identifier.
   * @returns Season details.
   */
  // eslint-disable-next-line class-methods-use-this
  async getSeason(seasonId: string): Promise<Season> {
    logger.warn(`${LP} getSeason not directly supported by NZOS API, returning empty season`);
    return {
      id: seasonId,
      type: 'season',
      title: '',
      images: [],
    } as Season;
  }

  /**
   * Get episode by ID.
   * @param id - Episode identifier.
   * @param authToken - Optional authentication token.
   * @returns Episode details.
   */
  async getEpisode(id: string, authToken?: string): Promise<Episode> {
    const baseUrl = getNZOSBaseUrl();
    const endpointPath = `/ovp/episodes/${id}`;
    const cacheKey = getCacheKey(baseUrl, endpointPath);
    const cachedResponse = await ovpCache.get<Episode>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await this.apiCall<Episode>(endpointPath, undefined, authToken);
    ovpCache.set(cacheKey, response, LONG_TTL);
    return response;
  }

  /**
   * Get episodes by show or season.
   * @param options - Episode options (showId or seasonId).
   * @param authToken - Optional authentication token.
   * @returns Paginated episode list.
   */
  async getEpisodes(
    options: EpisodeOptions,
    authToken?: string,
    clearCache?: boolean,
  ): Promise<OVPPaginationResponse<Episode>> {
    // If url is provided, fetch directly from that URL
    if (options.url) {
      const baseUrl = getNZOSBaseUrl();
      const cacheKey = getCacheKey(baseUrl, options.url);
      if (!clearCache) {
        const cachedResponse = await ovpCache.get<OVPPaginationResponse<Episode>>(cacheKey);
        if (cachedResponse) {
          return cachedResponse;
        }
      }
      const response = await this.fetch(options.url, options, authToken);
      const paginationResponse: OVPPaginationResponse<Episode> = {
        items: response.items as Episode[],
        totalCount: response.totalCount,
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        id: options.url,
        type: 'episodes',
      };

      ovpCache.set(cacheKey, paginationResponse, LONG_TTL);
      return paginationResponse;
    }

    return {
      items: [],
      totalCount: 0,
      pageNumber: 1,
      pageSize: 0,
      id: '',
      type: '',
    };
  }

  /**
   * Get paginated list of shows.
   * @param options - Pagination options.
   * @returns Paginated show list.
   */
  async getShows(options?: OVPOptions): Promise<OVPPaginationResponse<Show>> {
    const baseUrl = getNZOSBaseUrl();
    const endpoint = '/ovp/shows';
    const params = {
      pageSize: options?.pageSize,
      pageNumber: options?.pageNumber,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    };

    const cacheKey = getCacheKey(baseUrl, endpoint, params);
    const cachedResponse = await ovpCache.get<OVPPaginationResponse<Show>>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await this.apiCall<OVPPaginationResponse<Show>>(endpoint, params);
    ovpCache.set(cacheKey, response, MEDIUM_TTL);
    return response;
  }

  /**
   * Get all categories.
   * @returns Paginated category list.
   */
  async getCategories(): Promise<OVPPaginationResponse<Category>> {
    const baseUrl = getNZOSBaseUrl();
    const endpoint = '/ovp/categories';
    const cacheKey = getCacheKey(baseUrl, endpoint);
    const cachedResponse = await ovpCache.get<OVPPaginationResponse<Category>>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await this.apiCall<OVPPaginationResponse<Category>>(endpoint);
    ovpCache.set(cacheKey, response, LONG_TTL);
    return response;
  }

  /**
   * Get category by ID.
   * @param categoryId - Category identifier.
   * @returns Category details or undefined.
   */
  async getCategoryById(categoryId: string): Promise<Category | undefined> {
    const baseUrl = getNZOSBaseUrl();
    const endpointPath = `/ovp/categories/${categoryId}`;
    const cacheKey = getCacheKey(baseUrl, endpointPath);
    const cachedResponse = await ovpCache.get<Category>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    try {
      const response = await this.apiCall<Category>(endpointPath);
      ovpCache.set(cacheKey, response, LONG_TTL);
      return response;
    } catch (error) {
      logger.error(`${LP} Error fetching category ${categoryId}:`, error);
      return undefined;
    }
  }

  /**
   * Get asset by ID.
   * @param assetId - Asset identifier.
   * @returns Item details.
   */
  async getAsset(assetId: string): Promise<Item> {
    const baseUrl = getNZOSBaseUrl();
    const endpointPath = `/ovp/assets/${assetId}`;
    const cacheKey = getCacheKey(baseUrl, endpointPath);
    const cachedResponse = await ovpCache.get<Item>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await this.apiCall<Item>(endpointPath);
    ovpCache.set(cacheKey, response, LONG_TTL);
    return response;
  }

  /**
   * Get playlist by ID.
   * @param listId - Playlist identifier.
   * @param options - Pagination options.
   * @returns Paginated media item list.
   */
  async getPlaylist(listId: string, options?: OVPOptions): Promise<OVPPaginationResponse<MediaItem>> {
    const baseUrl = getNZOSBaseUrl();
    const endpointPath = `/ovp/playlists/${listId}`;
    const params = {
      pageSize: options?.pageSize,
      pageNumber: options?.pageNumber,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    };

    const cacheKey = getCacheKey(baseUrl, endpointPath, params);
    const cachedResponse = await ovpCache.get<OVPPaginationResponse<MediaItem>>(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await this.apiCall<OVPPaginationResponse<MediaItem>>(endpointPath, params);
    ovpCache.set(cacheKey, response, MEDIUM_TTL);
    return response;
  }

  /**
   * Add an item to the user's "My List" (watchlist).
   * @param itemId - The ID of the item to add.
   * @param itemType - The type of the item (e.g., 'movie', 'show', 'episode').
   * @param token - Authentication token.
   * @returns Promise that resolves when the item is added.
   */
  // eslint-disable-next-line class-methods-use-this
  async addToMyList(itemId: string, itemType: string, token: string): Promise<boolean> {
    const endpoint = '/my-list';

    try {
      // Use the NZOS API adapter which automatically handles authentication
      // The token parameter is passed via headers to override the default behavior
      const response = (await nzosApiCall(endpoint, {
        method: 'POST',
        body: {
          itemId,
          itemType,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })) as Response;

      if (response) {
        logger.info(`${LP} Successfully added item ${itemId} (${itemType}) to My List`);
        return true;
      }
      return false;
    } catch (error) {
      logger.error(`${LP} Error adding to My List:`, error);
      throw error;
    }
  }

  /**
   * Remove an item from the user's "My List" (watchlist).
   * @param itemId - The ID of the item to add.
   * @param token - Authentication token.
   * @returns Promise that resolves when the item is added.
   */
  // eslint-disable-next-line class-methods-use-this
  async removeFromMyList(itemId: string, token: string): Promise<boolean> {
    const endpoint = `/my-list/${itemId}`;

    try {
      // Use the NZOS API adapter which automatically handles authentication
      // The token parameter is passed via headers to override the default behavior
      const response = await nzosApiCall(endpoint, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response) {
        logger.info(`${LP} Successfully removed item ${itemId} from My List`);
        return true;
      }
      return false;
    } catch (error) {
      logger.error(`${LP} Error removing from My List:`, error);
      throw error;
    }
  }

  /**
   * Get all available genres.
   * Used for browse-by-genre functionality.
   * @returns Object with genres array and total count.
   */
  async getGenres(): Promise<{ genres: Genre[]; totalCount: number }> {
    const baseUrl = getNZOSBaseUrl();
    const endpoint = '/ovp/genres';
    const cacheKey = getCacheKey(baseUrl, endpoint);
    const cached = await ovpCache.get<{ genres: Genre[]; totalCount: number }>(cacheKey);
    if (cached) {
      logger.debug(`${LP} Returning cached genres`);
      return cached;
    }

    logger.debug(`${LP} Fetching genres from API`);

    try {
      const response = await this.apiCall<OVPPaginationResponse<Genre>>(endpoint);

      // Transform paginated response to expected format
      const result = {
        genres: response.items || [],
        totalCount: response.totalCount || 0,
      };

      await ovpCache.set(cacheKey, result, LONG_TTL);

      return result;
    } catch (error) {
      logger.error(`${LP} Error fetching genres:`, error);
      throw error;
    }
  }

  /**
   * Generic fetch method for dynamic query URLs from CMS.
   * Handles full API URLs returned by NZOS backend in container queries.
   * @param url - Full API URL or relative path to fetch.
   * @param options - Fetch options.
   * @param authToken - Optional authentication token.
   * @returns Paginated response with items.
   */
  async fetch(url: string, options?: OVPOptions, authToken?: string): Promise<OVPPaginationResponse<Item>> {
    try {
      // Parse the URL to extract path and query params
      const urlObj = new URL(url);
      const { pathname } = urlObj;

      // Extract endpoint from pathname (remove /v1 prefix if present)
      const endpoint = pathname.replace(/^\/v1/, '');

      // Convert URL search params to object
      const params: Record<string, unknown> = {};
      urlObj.searchParams.forEach((value, key) => {
        // Handle multiple values for same key (e.g., multiple id params)
        if (params[key]) {
          if (Array.isArray(params[key])) {
            (params[key] as string[]).push(value);
          } else {
            params[key] = [params[key] as string, value];
          }
        } else {
          params[key] = value;
        }
      });

      // Add pagination options if provided
      if (options?.pageSize) params.pageSize = options.pageSize;
      if (options?.pageNumber) params.pageNumber = options.pageNumber;
      if (options?.sortBy) params.sortBy = options.sortBy;
      if (options?.sortOrder) params.sortOrder = options.sortOrder;

      // Make the API call
      const response = await this.apiCall<OVPPaginationResponse<Item>>(endpoint, params, authToken);

      return response;
    } catch (error) {
      return {
        items: [],
        totalCount: 0,
        pageNumber: options?.pageNumber || 1,
        pageSize: options?.pageSize || 0,
        id: '',
        type: '',
      };
    }
  }

  /**
   * Appends query parameter to URL.
   * @param url - Target URL.
   * @param key - Parameter key.
   * @param value - Parameter value.
   */
  // eslint-disable-next-line class-methods-use-this
  private appendParam(url: URL, key: string, value: unknown): void {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.map((item) => url.searchParams.append(key, String(item)));
    } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      url.searchParams.append(key, String(value));
    }
  }

  /**
   * Builds URL with query parameters.
   * @param endpoint - API endpoint path.
   * @param params - Query parameters.
   * @returns Configured URL object.
   */
  private buildUrl(endpoint: string, params?: Record<string, unknown>): URL {
    const baseUrl = getNZOSBaseUrl();
    // Remove leading slash from endpoint to append properly
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    // Ensure baseUrl ends with slash for proper joining
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const url = new URL(cleanEndpoint, cleanBaseUrl);

    if (params) {
      Object.keys(params).map((key) => this.appendParam(url, key, params[key]));
    }

    return url;
  }

  private async apiCall<T>(endpoint: string, params?: Record<string, unknown>, authToken?: string): Promise<T> {
    const url = this.buildUrl(endpoint, params);
    logger.debug(`${LP} API call`, url.toString());

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      const errorMsg = `API error: ${response.status} ${response.statusText}`;
      logger.error(`${LP} ${errorMsg} - ${errorText}`);
      throw new Error(errorMsg);
    }

    const responseText = await response.text();
    return JSON.parse(responseText) as T;
  }
}

export { NzosOVPService };
